function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable no-unused-vars */
import React, { forwardRef, memo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { InputTag } from './InputTag';
import { Prefix } from './Prefix';
import { Suffix } from './Suffix';
import { TYPES, SIZES } from '../../../constants.js';
import ActionBar from '../ActionBar/ActionBar.js';
import styles from '@dtsl/css-input/dist/index.module.css';

// eslint-disable-next-line react/display-name
const TextInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    size = SIZES.MEDIUM,
    type,
    prefix = null,
    suffix = null,
    actions = [],
    readOnly = false,
    isError,
    className = '',
    isSuccess,
    stretch = false,
    disabled = false,
    actionsClass = '',
    actionsStyle = {},
    maxCharCount = null,
    currCharCount = 0,
    leftAddon = null,
    rightAddon = null,
    name,
    validationText,
    accessiblePrefixText = 'prefix',
    accessibleSuffixText = 'suffix',
    ...restProps
  } = props;
  const inputClasses = classNames({
    [styles['sib-input']]: true,
    [styles[`sib-input__type-${type}`]]: type === TYPES.SEARCH || type === TYPES.NUMBER,
    [styles[`sib-input--${size}`]]: size === SIZES.SMALL,
    [styles['sib-input--disabled']]: disabled,
    [styles['sib-input--error']]: isError,
    [styles['sib-input--success']]: isSuccess,
    [styles['sib-input--readonly']]: readOnly,
    [styles['sib-input--has-left-addon']]: leftAddon && (type === TYPES.TEXT || type === TYPES.NUMBER),
    [styles['sib-input--has-right-addon']]: rightAddon && (type === TYPES.TEXT || type === TYPES.NUMBER),
    [styles['sib-input__with-actions']]: actions && actions.length > 0,
    [className]: !!className
  });
  const inputControlClasses = classNames({
    [styles['sib-input-common-control']]: true,
    [styles['sib-input__control']]: true,
    [styles[`sib-input__control--${size}`]]: size === SIZES.SMALL
  });
  const maxLength = !readOnly && !disabled && type === TYPES.TEXT ? maxCharCount : null;
  return /*#__PURE__*/React.createElement("div", {
    className: inputClasses
  }, /*#__PURE__*/React.createElement(Prefix, {
    prefix: prefix,
    id: id,
    accessiblePrefixText: accessiblePrefixText
  }), /*#__PURE__*/React.createElement(InputTag, _extends({}, restProps, {
    ref: ref,
    id: id,
    type: type,
    name: name,
    isError: isError,
    isSuccess: isSuccess,
    readOnly: readOnly,
    disabled: disabled,
    maxLength: maxLength,
    inputControlClasses: inputControlClasses,
    prefix: prefix,
    suffix: suffix,
    validationText: validationText
  })), /*#__PURE__*/React.createElement(Suffix, {
    suffix: suffix,
    size: size,
    id: id,
    accessibleSuffixText: accessibleSuffixText
  }), /*#__PURE__*/React.createElement(ActionBar, {
    id: id,
    actions: actions,
    maxCharCount: maxLength,
    currCharCount: currCharCount,
    actionsClass: actionsClass,
    actionsStyle: actionsStyle,
    stretch: stretch,
    size: size,
    disableActions: disabled || readOnly
  }));
});
TextInput.propTypes = {
  size: PropTypes.string,
  disabled: PropTypes.bool,
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  maxCharCount: PropTypes.number,
  isError: PropTypes.bool,
  isSuccess: PropTypes.bool,
  actions: PropTypes.array,
  readOnly: PropTypes.bool,
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(TYPES)),
  currCharCount: PropTypes.number,
  actionsClass: PropTypes.string,
  actionsStyle: PropTypes.object,
  stretch: PropTypes.bool,
  className: PropTypes.string,
  leftAddon: PropTypes.object,
  rightAddon: PropTypes.object,
  name: PropTypes.string,
  validationText: PropTypes.string,
  accessiblePrefixText: PropTypes.string,
  accessibleSuffixText: PropTypes.string
};
export default /*#__PURE__*/memo(TextInput);